// src/screens/SolicitarCotizacionScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebaseConfig';
import { doc, getDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoCloudUploadOutline, IoImageOutline, IoCloseCircle } from 'react-icons/io5';
import './SolicitarCotizacionScreen.css';

function SolicitarCotizacionScreen() {
  const navigate = useNavigate();
  const { proveedorId } = useParams();
  const { currentUser, userData } = useAuth();

  const [proveedor, setProveedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [tipoServicio, setTipoServicio] = useState('Reparacion');
  const [descripcion, setDescripcion] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    const fetchProveedor = async () => {
      if (!proveedorId) {
        setError("No se especificó un proveedor.");
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, "market_proveedores", proveedorId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProveedor(docSnap.data());
        } else {
          setError("Proveedor no encontrado.");
        }
      } catch (e) {
        setError("Error al cargar datos del proveedor.");
      }
      setLoading(false);
    };
    fetchProveedor();
  }, [proveedorId]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) {
      alert("Máximo 3 archivos permitidos.");
      return;
    }
    setArchivos(files);
    const newPreviews = files
      .filter(file => file.type.startsWith('image/'))
      .map(file => URL.createObjectURL(file));
    setPreviews(newPreviews);
  };

  const removeItem = () => {
    setArchivos([]);
    setPreviews([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!descripcion) {
      setError("Por favor, incluye una descripción de tu problema.");
      return;
    }
    if (!currentUser || !userData) {
      setError("No se pudo identificar al usuario.");
      return;
    }

    setEnviando(true);
    setError('');

    try {
      let nombreCondominio = userData.condominioId;
      try {
        const condoRef = doc(db, "condominios", userData.condominioId);
        const condoSnap = await getDoc(condoRef);
        if (condoSnap.exists()) {
          nombreCondominio = condoSnap.data().nombre;
        }
      } catch (e) {
        console.error("No se pudo obtener el nombre del condominio: ", e);
      }

      const urlsAdjuntos = [];
      for (const file of archivos) {
        const storageRef = ref(storage, `cotizaciones/${currentUser.uid}/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snapshot.ref);
        urlsAdjuntos.push(url);
      }

      await addDoc(collection(db, "cotizaciones"), {
        proveedorId: proveedorId,
        residenteId: currentUser.uid,
        condominioId: userData.condominioId,
        nombreResidente: userData.nombre,
        unidadResidente: userData.numeroCasa,
        nombreCondominio: nombreCondominio,
        nombreProveedor: proveedor.nombrePublico,
        categoria: proveedor.categorias ? proveedor.categorias[0] : 'Otros',
        logoUrl: proveedor.logoUrl || null,
        tipoServicio: tipoServicio,
        descripcion: descripcion,
        adjuntosUrls: urlsAdjuntos,
        estatus: "pendiente_proveedor",
        fechaCreacion: Timestamp.now(),
        costoMin: null,
        costoMax: null,
        comentariosProveedor: "",
        validezCotizacion: null,
        tiempoServicio: ""
      });

      setEnviando(false);
      alert("¡Solicitud de cotización enviada con éxito!");

      // Intentar navegar a la lista de categoría, sino al marketplace general
      const targetCategory = proveedor.categorias ? proveedor.categorias[0] : 'Otros';
      navigate(`/marketplace/lista/${targetCategory.toLowerCase()}`);

    } catch (e) {
      console.error("Error al enviar cotización: ", e);
      setError("Error al enviar la solicitud.");
      setEnviando(false);
    }
  };

  if (loading) return <div className="quote-loading"><p>Cargando formulario...</p></div>;

  return (
    <div className="quote-screen-container">
      {/* Header */}
      <div className="quote-header-group">
        <button onClick={() => navigate(-1)} className="quote-back-btn">
          <IoArrowBack size={24} />
        </button>
        <h1 className="quote-header-title">Solicitar Cotización</h1>
      </div>

      {/* Main Card */}
      <div className="quote-form-card">

        {/* Banner */}
        <div className="quote-provider-banner">
          Estás contactando a: <span className="quote-provider-name">{proveedor?.nombrePublico}</span>
        </div>

        {error && <div className="quote-error-msg">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="quote-form-group">
            <label className="quote-label">¿Qué tipo de servicio necesitas?</label>
            <select
              value={tipoServicio}
              onChange={(e) => setTipoServicio(e.target.value)}
              className="quote-select"
            >
              <option value="Diagnostico">Diagnóstico / Revisión Inicial</option>
              <option value="Reparacion">Reparación Específica</option>
              <option value="Mantenimiento">Mantenimiento Preventivo</option>
              <option value="Instalacion">Instalación Nueva</option>
            </select>
          </div>

          <div className="quote-form-group">
            <label className="quote-label">Describe tu problema detalladamente</label>
            <textarea
              className="quote-textarea"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej: Tengo una fuga en la tubería del baño principal. Necesito que vengan lo antes posible..."
              required
            />
          </div>

          <div className="quote-form-group">
            <label className="quote-label">Evidencia (Fotos/Video) - Opcional</label>

            {/* Custom Upload Area */}
            <label className="quote-upload-area">
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileChange}
                accept="image/*,video/*"
              />
              <div className="upload-icon"><IoCloudUploadOutline /></div>
              <div className="upload-text">Haz clic o arrastra archivos aquí</div>
              <div style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Máx 3 archivos</div>
            </label>

            {/* File Previews */}
            {previews.length > 0 && (
              <div className="file-preview">
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <IoImageOutline size={20} color="#2563eb" />
                  <span>{archivos.length} archivo(s) seleccionado(s)</span>
                </div>
                <div className="remove-file-btn" onClick={removeItem}>
                  <IoCloseCircle />
                </div>
              </div>
            )}
            {previews.length > 0 && (
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px', overflowX: 'auto', paddingBottom: '5px' }}>
                {previews.map((src, i) => (
                  <img key={i} src={src} alt="preview" style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' }} />
                ))}
              </div>
            )}
          </div>

          <button type="submit" className="quote-submit-btn" disabled={enviando}>
            {enviando ? 'Enviando Solicitud...' : 'Enviar Solicitud'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SolicitarCotizacionScreen;